import { getContentMessageArea } from "./skyward.ui.contentMessageArea";
import debounce from "./skyward.ui.debounce";
import { getSvg } from "./skyward.ui.svg";
import { setAriaInvalid } from "./skyward.ui.validation";

const classNames =
{
    errorClass: "js-error-control--preSaveValidation",
    warningClass: "js-warn-control--preSaveValidation",
    infoClass: "js-info-control--preSaveValidation",
    warnIgnoreClass: "js-warn-control--preSaveIgnore",
    errorControlClass: "error-control",
    warnControlClass: "warning-control",
    infoControlClass: "info-control",
    errorValidateClass: "error-text",
    warningValidateClass: "warning-text",
    infoValidateClass: "info-text",
    infoInvalidFieldClass: "js-info-text--invalidField",
    warningInvalidFieldClass: "js-warning-text--invalidField",
    errorInvalidFieldClass: "js-error-text--invalidField",
    changeTrackingRoot: "js-changeTrackingRoot",
    comboBox: "comboBoxSet",
    datepicker: "datepicker",
    dmsBackingData: "dmsBackingData",
    validationList: "validationList",
    validationSpan: "validationSpan"
};

const eventNames =
{
    blur: "blur.preSaveValidation",
    change: "change.preSaveValidation",
    dirty: "dirty.preSaveValidation",
};

const _errorLi = $("<li>",
{
    addClass: [classNames.errorValidateClass, classNames.errorInvalidFieldClass],
    append: $("<span>",
    {
        append:
        [
            getSvg("Error", "Error").addClass(["alertError", "svgNoHover"]), $("<span>",
            {
                addClass: classNames.validationSpan
            })
        ]
    })
});

const _warnLi = $("<li>",
{
    addClass: [classNames.warningValidateClass, classNames.warningInvalidFieldClass],
    append: $("<span>",
    {
        append:
        [
            getSvg("Warning", "Warning").addClass(["alertWarning", "svgNoHover"]), $("<span>",
            {
                addClass: classNames.validationSpan
            })
        ]
    })
});

const _infoLi = $("<li>",
{
    addClass: [classNames.infoValidateClass, classNames.infoInvalidFieldClass],
    append: $("<span>",
    {
        append:
        [
            getSvg("Info").addClass(["alertInfo", "svgNoHover"]), $("<span>",
            {
                addClass: classNames.validationSpan
            })
        ]
    })
});

function getValidationElements(field: JQuery<HTMLElement>): ValidationElements | undefined
{
    let dataValidateMessageFor: JQuery<HTMLElement> | undefined;
    let dataValidateTextFor: JQuery<HTMLElement> | undefined;
    let validationElements: ValidationElements | undefined;
    let fieldContainer = field.closest(".fieldWrapper");
    if (fieldContainer.length === 0)
    {
        return undefined;
    }

    let key = field.attr("id");
    if (key)
    {
        dataValidateMessageFor = fieldContainer.find(`[data-validate-message-for="${key}"]`);
        dataValidateTextFor = fieldContainer.find(`[for="${key}"]`);
    }
    if (!dataValidateMessageFor || dataValidateMessageFor.length === 0)
    {
        key = field.attr('data-validate-control-for');
        dataValidateMessageFor = fieldContainer.find(`[data-validate-message-for="${key}"]`);
        dataValidateTextFor = fieldContainer.find(`[data-validate-text-for="${key}"]`);
    }

    if (dataValidateMessageFor && dataValidateTextFor)
    {
        validationElements =
        {
            dataValidateMessageFor: dataValidateMessageFor,
            dataValidateTextFor: dataValidateTextFor
        };
    }

    return validationElements;
}

function getCurrentValidationState(dataValidateMessageFor: JQuery<HTMLElement>): ValidationState
{
    let showInfoClass = false;
    let showWarnClass = false;
    let showErrorClass = false;

    let existingPreSaveInfoMessages: ExistingPreSaveValidation[] = [];
    let existingPreSaveWarnMessages: ExistingPreSaveValidation[] = [];
    let existingPreSaveErrorMessages: ExistingPreSaveValidation[] = [];

    if (dataValidateMessageFor.length !== 0)
    {
        const oldInfoMessage = dataValidateMessageFor.find(`.${classNames.infoInvalidFieldClass}`);
        const oldWarnMessage = dataValidateMessageFor.find(`.${classNames.warningInvalidFieldClass}`);
        const oldErrorMessage = dataValidateMessageFor.find(`.${classNames.errorInvalidFieldClass}`);

        showInfoClass = dataValidateMessageFor.find(`.${classNames.infoValidateClass}`).not(oldInfoMessage).length > 0;
        showWarnClass = dataValidateMessageFor.find(`.${classNames.warningValidateClass}`).not(oldWarnMessage).length > 0;
        showErrorClass = dataValidateMessageFor.find(`.${classNames.errorValidateClass}`).not(oldErrorMessage).length > 0;

        existingPreSaveInfoMessages = collectExistingValidation(oldInfoMessage, "info");
        existingPreSaveWarnMessages = collectExistingValidation(oldWarnMessage, "warning");
        existingPreSaveErrorMessages = collectExistingValidation(oldErrorMessage, "error");
    }

    const stateSettings: ValidationState =
    {
        showInfo: showInfoClass,
        showWarning: showWarnClass,
        showError: showErrorClass,
        preSaveInfoCount: existingPreSaveInfoMessages.length,
        preSaveErrorCount: existingPreSaveWarnMessages.length,
        preSaveWarnCount: existingPreSaveErrorMessages.length,
        existingPreSaveInfo: existingPreSaveInfoMessages,
        existingPreSaveWarn: existingPreSaveWarnMessages,
        existingPreSaveError: existingPreSaveErrorMessages
    };

    return stateSettings;
}

function collectExistingValidation(messageElements: JQuery<HTMLElement>, severityType: PreSaveValidationSeverityType): ExistingPreSaveValidation[]
{
    let existingPreSaveMessages: ExistingPreSaveValidation[] = [];

    if (messageElements.length > 0)
    {
        messageElements.each(function (this: HTMLElement)
        {
            const liElement = $(this);
            const messageElement = liElement.find(`.${classNames.validationSpan}`);
            const messageText = messageElement.text();

            if (messageText !== "")
            {
                const message: ExistingPreSaveValidation =
                {
                    severityType: severityType,
                    message: messageText,
                    toRemove: true,
                    liElement: liElement
                };
                existingPreSaveMessages.push(message);
            }
        });
    }

    return existingPreSaveMessages;
}

function addPreSaveValidation(dataValidateMessageFor: JQuery<HTMLElement>, validationTemplateDetails: ValidationTemplateDetails[]): void
{
    let ul = dataValidateMessageFor.find(`ul.${classNames.validationList}`);
    if (ul.length === 0)
    {
        ul = $("<ul>", { addClass: classNames.validationList }).appendTo(dataValidateMessageFor);
    }

    for (const detail of validationTemplateDetails)
    {
        const liElementClone = detail.template.clone();
        liElementClone.find(`.${classNames.validationSpan}`).text(detail.message);
        liElementClone.appendTo(ul);
    }
}

function removePreSaveValidation(dataValidateMessageFor: JQuery<HTMLElement>, existingPreSaveInfo: ExistingPreSaveValidation[], existingPreSaveWarn: ExistingPreSaveValidation[], existingPreSaveError: ExistingPreSaveValidation[]): void
{
    for (const info of existingPreSaveInfo)
    {
        info.liElement.remove();
    }

    for (const warn of existingPreSaveWarn)
    {
        warn.liElement.remove();
    }

    for (const error of existingPreSaveError)
    {
        error.liElement.remove();
    }

    const ul = dataValidateMessageFor.find(`ul.${classNames.validationList}`);
    if (ul.children().length === 0)
    {
        ul.remove();
    }
}

function setupInitialValidationStateSettings(dataValidateMessageFor: JQuery<HTMLElement>): ValidationStateSettings
{
    const stateSettings = getCurrentValidationState(dataValidateMessageFor);

    const initialState: ValidationStateSettings =
    {
        warnChange: 0,
        errorChange: 0,
        showInfo: stateSettings.showInfo,
        showWarning: stateSettings.showWarning,
        showError: stateSettings.showError,
        preSaveInfoCount: stateSettings.preSaveInfoCount,
        preSaveWarnCount: stateSettings.preSaveWarnCount,
        preSaveErrorCount: stateSettings.preSaveWarnCount,
        removeIgnoredWarningMarker: true,
        existingPreSaveInfo: stateSettings.existingPreSaveInfo,
        existingPreSaveWarn: stateSettings.existingPreSaveWarn,
        existingPreSaveError: stateSettings.existingPreSaveError,
        liElementTemplateDetails: []
    };

    return initialState;
}

function createModifiedValidationStateSettings(initialState: ValidationStateSettings, toggle: boolean, validations: PreSaveValidation[]): ValidationStateSettings
{
    const stateChanges = { ...initialState };
    let warnChangeCount = 0;
    let errorChangeCount = 0;

    if (toggle)
    {
        for (const validation of validations)
        {
            switch (validation.severityType)
            {
                case "info":
                    stateChanges.showInfo = true;

                    const infoTemplateDetails: ValidationTemplateDetails =
                    {
                        template: _infoLi,
                        message: validation.message
                    };
                    stateChanges.liElementTemplateDetails.push(infoTemplateDetails);

                    const existingInfoMessage = getExistingValidationMessage(initialState.existingPreSaveInfo, validation.message);
                    if (existingInfoMessage)
                    {
                        existingInfoMessage.toRemove = false;
                    }
                    break;
                case "warning":
                    stateChanges.showWarning = true;

                    const warnTemplateDetails: ValidationTemplateDetails =
                    {
                        template: _warnLi,
                        message: validation.message
                    };
                    stateChanges.liElementTemplateDetails.push(warnTemplateDetails);

                    const existingWarnMessage = getExistingValidationMessage(initialState.existingPreSaveWarn, validation.message);
                    if (existingWarnMessage)
                    {
                        existingWarnMessage.toRemove = false;
                    }
                    else
                    {
                        warnChangeCount++;
                        stateChanges.removeIgnoredWarningMarker = false;
                    }
                    break;
                case "error":
                    stateChanges.showError = true;

                    const errorTemplateDetails: ValidationTemplateDetails =
                    {
                        template: _errorLi,
                        message: validation.message
                    };
                    stateChanges.liElementTemplateDetails.push(errorTemplateDetails);

                    const existingErrorMessage = getExistingValidationMessage(initialState.existingPreSaveError, validation.message);
                    if (existingErrorMessage)
                    {
                        existingErrorMessage.toRemove = false;
                    }
                    else
                    {
                        errorChangeCount++;
                    }
                    break;
            }
        }
    }

    for (const existingWarning of initialState.existingPreSaveWarn)
    {
        if (existingWarning.toRemove)
        {
            warnChangeCount--;
        }
    }

    for (const existingError of initialState.existingPreSaveError)
    {
        if (existingError.toRemove)
        {
            errorChangeCount--;
        }
    }

    stateChanges.warnChange = warnChangeCount;
    stateChanges.errorChange = errorChangeCount;

    return stateChanges;
}

function getExistingValidationMessage(existingValidation: ExistingPreSaveValidation[], message: string): ExistingPreSaveValidation | undefined
{
    for (const validation of existingValidation)
    {
        if (validation.message === message)
        {
            return validation;
        }
    }
    return undefined;
}

function changeValidationState(changedState: ValidationStateSettings, field: JQuery<HTMLElement>, dataValidateMessageFor: JQuery<HTMLElement>, dataValidateTextFor: JQuery<HTMLElement>): void
{
    const cma = getContentMessageArea(field.closest(`.${classNames.changeTrackingRoot}`).parent());

    if (changedState.existingPreSaveInfo.length > 0 || changedState.existingPreSaveWarn.length > 0 || changedState.existingPreSaveError.length > 0)
    {
        removePreSaveValidation(dataValidateMessageFor, changedState.existingPreSaveInfo, changedState.existingPreSaveWarn, changedState.existingPreSaveError);
    }

    if (changedState.liElementTemplateDetails.length > 0)
    {
        addPreSaveValidation(dataValidateMessageFor, changedState.liElementTemplateDetails);
    }

    cma?.modifyValidationCount(changedState.errorChange, changedState.warnChange);

    field.toggleClass([classNames.infoClass, classNames.infoControlClass], changedState.showInfo)
        .toggleClass([classNames.warningClass, classNames.warnControlClass], changedState.showWarning)
        .toggleClass([classNames.errorClass, classNames.errorControlClass], changedState.showError);

    setAriaInvalid(field, changedState.showError);

    dataValidateTextFor.toggleClass(classNames.infoValidateClass, changedState.showInfo).toggleClass(classNames.warningValidateClass, changedState.showWarning).toggleClass(classNames.errorValidateClass, changedState.showError);

    if (changedState.removeIgnoredWarningMarker)
    {
        field.removeClass(classNames.warnIgnoreClass);
    }
}

function toggleValidation(field: JQuery<HTMLElement>, toggle: boolean, preSaveValidation: PreSaveValidation[] = []): void
{
    const validationElements = getValidationElements(field);
    if (!validationElements)
    {
        return;
    }

    const dataValidateMessageFor = validationElements.dataValidateMessageFor;
    const dataValidateTextFor = validationElements.dataValidateTextFor;

    const initialStateSettings = setupInitialValidationStateSettings(dataValidateMessageFor);
    const changedStateSettings = createModifiedValidationStateSettings(initialStateSettings, toggle, preSaveValidation);

    changeValidationState(changedStateSettings, field, dataValidateMessageFor, dataValidateTextFor);
}

function handleChangedField(field: JQuery<HTMLElement>, result?: PreSaveValidationResult): void
{
    if (result?.showValidation)
    {
        toggleValidation(result.fieldElement, true, result.validation);
    }
    else
    {
        toggleValidation(field, false);
    }
}

async function onFieldChangeAsync(field: JQuery<HTMLElement>, validationCallback: FieldValidationCallBack): Promise<void>
{
    const validationResult = await validationCallback(field);
    handleChangedField(field, validationResult);
}

async function onContainerChangeAsync(fields: JQuery<HTMLElement>, validationCallback: ContainerValidationCallBack): Promise<void>
{
    const callBackFields: JQuery<HTMLElement>[] = [];
    fields.each(function (this: HTMLElement)
    {
        const field = $(this);
        callBackFields.push(field);
    });

    const validationResult = await validationCallback(callBackFields);
    for (const fieldValidation of validationResult.validation)
    {
        handleChangedField(fieldValidation.fieldElement, fieldValidation);
    }
}

function handleRegisterValidation(field: JQuery<HTMLElement>, validationCallback: FieldValidationCallBack): void
{
    const debounceValidation = debounce(() =>
    {
        onFieldChangeAsync(field, validationCallback);
    }, 300, 1000);

    setupEventsOnField(field, debounceValidation);
}

function handleRegisterContainer(container: JQuery<HTMLElement>, selector: string, validationCallback: ContainerValidationCallBack)
{
    const fields = container.find(selector);

    const debounceValidation = debounce(() =>
    {
        onContainerChangeAsync(fields, validationCallback);
    }, 300, 1000);

    fields.each(function (this: HTMLElement)
    {
        const field = $(this);
        setupEventsOnField(field, debounceValidation);
    });
}

function setupEventsOnField(field: JQuery<HTMLElement>, debounceValidation: () => void): void
{
    if (field.hasClass(classNames.datepicker))
    {
        field.off(eventNames.dirty).on(eventNames.dirty, debounceValidation);
        field.off(eventNames.change).on(eventNames.change, debounceValidation);
    }
    else if (field.hasClass(classNames.comboBox) || field.hasClass(classNames.dmsBackingData))
    {
        field.off(eventNames.change).on(eventNames.change, debounceValidation);
    }
    else
    {
        field.off(eventNames.blur).on(eventNames.blur, debounceValidation);
    }
}

export function getPreSaveValidationHelper(): PreSaveValidationHelper
{
    const preSaveValidationHelper =
    {
        showValidation: function (field: JQuery<HTMLElement>, validation: PreSaveValidation[])
        {
            toggleValidation(field, true, validation);
        },
        removeValidation: function (field: JQuery<HTMLElement>)
        {
            toggleValidation(field, false);
        },
        hasError: function (field: JQuery<HTMLElement>)
        {
            return field.hasClass(classNames.errorClass);
        },
        hasWarning: function (field: JQuery<HTMLElement>)
        {
            return field.hasClass(classNames.warningClass);
        },
        hasInfo: function (field: JQuery<HTMLElement>)
        {
            return field.hasClass(classNames.infoClass);
        },
        registerFieldValidation: function (field: JQuery<HTMLElement>, validationCallback: FieldValidationCallBack)
        {
            handleRegisterValidation(field, validationCallback);
        },
        registerContainerValidation: function (container: JQuery<HTMLElement>, selector: string, validationCallback: ContainerValidationCallBack)
        {
            handleRegisterContainer(container, selector, validationCallback);
        }
    };

    return preSaveValidationHelper;
}

export interface PreSaveValidationHelper
{
    showValidation: (field: JQuery<HTMLElement>, validation: PreSaveValidation[]) => void;
    removeValidation: (field: JQuery<HTMLElement>) => void;
    hasError: (field: JQuery<HTMLElement>) => boolean;
    hasWarning: (field: JQuery<HTMLElement>) => boolean;
    hasInfo: (field: JQuery<HTMLElement>) => boolean;
    registerFieldValidation: (field: JQuery<HTMLElement>, validationCallback: FieldValidationCallBack) => void;
    registerContainerValidation: (container: JQuery<HTMLElement>, selector: string, validationCallback: ContainerValidationCallBack) => void;
}

type FieldValidationCallBack = ((field: JQuery<HTMLElement>) => Promise<PreSaveValidationResult>);
type ContainerValidationCallBack = ((fields: JQuery<HTMLElement>[]) => Promise<ContainerPreSaveValidationResult>);

export interface PreSaveValidationResult
{
    showValidation: boolean;
    validation: PreSaveValidation[];
    fieldElement: JQuery<HTMLElement>;
}

export interface ContainerPreSaveValidationResult
{
    validation: PreSaveValidationResult[];
}

export interface PreSaveValidation
{
    severityType: PreSaveValidationSeverityType;
    message: string;
}

interface ExistingPreSaveValidation extends PreSaveValidation
{
    toRemove: boolean;
    liElement: JQuery<HTMLElement>;
}

export type PreSaveValidationSeverityType = "error" | "warning" | "info";

interface ValidationElements
{
    dataValidateMessageFor: JQuery<HTMLElement>;
    dataValidateTextFor: JQuery<HTMLElement>;
}

interface ValidationState
{
    showInfo: boolean;
    showWarning: boolean;
    showError: boolean;
    preSaveInfoCount: number;
    preSaveWarnCount: number;
    preSaveErrorCount: number;
    existingPreSaveInfo: ExistingPreSaveValidation[];
    existingPreSaveWarn: ExistingPreSaveValidation[];
    existingPreSaveError: ExistingPreSaveValidation[];
}

interface ValidationStateSettings
{
    warnChange: number;
    errorChange: number;
    showInfo: boolean;
    showWarning: boolean;
    showError: boolean;
    preSaveInfoCount: number;
    preSaveWarnCount: number;
    preSaveErrorCount: number;
    removeIgnoredWarningMarker: boolean;
    existingPreSaveInfo: ExistingPreSaveValidation[];
    existingPreSaveWarn: ExistingPreSaveValidation[];
    existingPreSaveError: ExistingPreSaveValidation[];
    liElementTemplateDetails: ValidationTemplateDetails[];
}

interface ValidationTemplateDetails
{
    template: JQuery<HTMLElement>;
    message: string;
}